import { useEffect, useState } from 'react';
import { toggleStudioLike } from '@/api/studios';
import { isStudioLiked, setStudioLiked } from '@/lib/likes';

interface LikeButtonProps {
  studioId: number;
  initialCount: number;
  aiIdPrefix?: string;
}

export function LikeButton({ studioId, initialCount, aiIdPrefix = 'studio-like-button' }: LikeButtonProps) {
  const [liked, setLiked] = useState(() => isStudioLiked(studioId));
  const [likeCount, setLikeCount] = useState(initialCount);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLiked(isStudioLiked(studioId));
    setLikeCount(initialCount);
  }, [initialCount, studioId]);

  async function handleToggle() {
    if (isSaving) {
      return;
    }

    const previousLiked = liked;
    const previousCount = likeCount;
    const nextLiked = !liked;

    setIsSaving(true);
    setLiked(nextLiked);
    setLikeCount((count) => (nextLiked ? count + 1 : Math.max(0, count - 1)));
    setStudioLiked(studioId, nextLiked);

    try {
      const response = await toggleStudioLike(studioId, nextLiked);
      setLiked(response.liked);
      setLikeCount(response.likeCount);
      setStudioLiked(studioId, response.liked);
    } catch (error) {
      console.warn('Failed to update studio like count.', error);
      setLiked(previousLiked);
      setLikeCount(previousCount);
      setStudioLiked(studioId, previousLiked);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleToggle}
      aria-pressed={liked}
      aria-label={`좋아요 ${liked ? '취소' : '추가'}, 현재 ${likeCount}개`}
      disabled={isSaving}
      className={`inline-flex h-9 items-center justify-center gap-1 rounded-lg border px-2 text-sm font-semibold transition ${
        liked
          ? 'border-rose-200 bg-rose-50 text-rose-600'
          : 'border-slate-200 bg-white text-slate-600 hover:border-rose-200 hover:text-rose-500'
      } ${isSaving ? 'opacity-70' : ''} focus:outline-none focus-visible:ring-4 focus-visible:ring-rose-100`}
      data-ai-id={aiIdPrefix}
    >
      <span className="inline-flex" data-ai-id={`${aiIdPrefix}-icon-wrapper`}>
        <svg
          viewBox="0 0 24 24"
          fill={liked ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.9"
          aria-hidden="true"
          className="size-4"
          data-ai-id={`${aiIdPrefix}-icon`}
        >
          <path
            d="M12 20.2s-7.2-4.35-8.55-9.22C2.52 7.58 4.63 5 7.42 5c1.67 0 3.03.82 3.85 2.02C12.1 5.82 13.46 5 15.13 5c2.79 0 4.9 2.58 3.96 5.98C17.73 15.85 12 20.2 12 20.2Z"
            strokeLinecap="round"
            strokeLinejoin="round"
            data-ai-id={`${aiIdPrefix}-icon-heart-path`}
          />
        </svg>
      </span>
      <span className="tabular-nums" data-ai-id={`${aiIdPrefix}-count-value-text`}>
        {likeCount}
      </span>
    </button>
  );
}
